import { Controller } from "@hotwired/stimulus"
import { debounce } from "../util"

// Connects to data-controller="table-checkboxes"
export default class extends Controller {
  static targets = ["table", "checkbox", "counter", "filtersForm", "bulkActionsPanel"]
  static values = { idsUrl: String }

  allIds = []
  selectedIds = []
  ALL = "all"
  isIndeterminate = false
  pendingFilterChange = false
  reapplySelectAll = false
  selectionVersion = 0
  refreshVersion = 0

  initialize() {
    const FRAME_SWAP_DEBOUNCE_MS = 50

    this.clearCheckboxes = this.clearCheckboxes.bind(this)
    this.handleFiltersChanged = this.handleFiltersChanged.bind(this)
    this.scheduleRefresh = debounce(this.refreshCheckboxStates.bind(this), FRAME_SWAP_DEBOUNCE_MS)
  }

  connect() {
    document.addEventListener("table-checkboxes:clear", this.clearCheckboxes)
    this.element.addEventListener("filters:changed", this.handleFiltersChanged)
  }

  disconnect() {
    document.removeEventListener("table-checkboxes:clear", this.clearCheckboxes)
    this.element.removeEventListener("filters:changed", this.handleFiltersChanged)
    this.scheduleRefresh.cancel()
  }

  tableTargetConnected() {
    this.observeTableChanges()
    this.scheduleRefresh()
  }

  tableTargetDisconnected() {
    this.observer?.disconnect()
  }

  observeTableChanges() {
    this.observer = new MutationObserver(() => {
      this.scheduleRefresh()
    })
    this.observer.observe(this.tableTarget, { childList: true })
  }

  handleFiltersChanged() {
    this.selectionVersion += 1
    this.pendingFilterChange = true
    this.reapplySelectAll = this.areAllSelected()
  }

  async refreshCheckboxStates() {
    const refreshVersion = this.bumpRefreshVersion()
    const selectionVersion = this.selectionVersion

    const fetchedIds = await this.fetchAllIds()

    if (this.isStaleRefresh(refreshVersion, selectionVersion)) return

    this.applyFetchedIds(fetchedIds)

    if (this.consumePendingFilterChange()) {
      this.applyFilterChangeSelection()
    } else {
      this.keepStillMatchingSelection()
    }

    this.syncCheckboxesToSelection()

    if (this.checkboxTargets.length)
      this.updateSelectAllCheckbox()

    this.updateSelectedCounter()
    this.updateBulkActionsPanel()
    this.dispatchSelectionChange()
  }

  recordManualSelectionChange() {
    this.selectionVersion += 1
    this.pendingFilterChange = false
    this.reapplySelectAll = false
  }

  bumpRefreshVersion() {
    this.refreshVersion += 1

    return this.refreshVersion
  }

  isStaleRefresh(refreshVersion, selectionVersion) {
    return refreshVersion !== this.refreshVersion || this.isStaleSelection(selectionVersion)
  }

  isStaleSelection(selectionVersion) {
    return selectionVersion !== this.selectionVersion
  }

  consumePendingFilterChange() {
    const wasPending = this.pendingFilterChange
    this.pendingFilterChange = false

    return wasPending
  }

  applyFetchedIds(fetchedIds) {
    this.allIds = fetchedIds || []
  }

  isFailedFetch(fetchedIds) {
    return fetchedIds === null
  }

  applyFilterChangeSelection() {
    this.selectedIds = this.reapplySelectAll ? [ ...this.allIds ] : []
    this.reapplySelectAll = false
  }

  keepStillMatchingSelection() {
    this.selectedIds = this.selectedIds.filter(id => this.allIds.includes(id))
  }

  syncCheckboxesToSelection() {
    this.checkboxTargets.forEach(checkbox => {
      checkbox.checked = this.selectedIds.includes(checkbox.value)
    })
  }

  async toggle(event) {
    this.recordManualSelectionChange()

    const checkbox = event.target
    const value = this.parseCheckboxValue(checkbox.value)
    const isChecked = checkbox.checked

    if (this.isIndeterminate && value === this.ALL) {
      this.handleIndeterminate()
      return
    }

    if (isChecked) {
      await this.handleSelect(value)
    } else {
      this.handleDeselect(value)
    }

    this.updateSelectAllCheckbox()
    this.updateSelectedCounter()
    this.updateBulkActionsPanel()
    this.dispatchSelectionChange()
  }

  async handleSelect(value) {
    if (value === this.ALL) {
      await this.selectAll()
      return
    }
    this.selectedIds.push(value.toString())
    this.updateSelectAllCheckbox()
  }

  handleDeselect(value) {
    if (value === this.ALL) {
      this.deselectAll()
      return
    }
    this.selectedIds = this.selectedIds.filter(id => id != value)
    this.updateSelectAllCheckbox()
  }

  handleIndeterminate() {
    this.deselectAll()
    this.isIndeterminate = false
    this.updateSelectedCounter()
    this.updateBulkActionsPanel()
    this.dispatchSelectionChange()
  }

  clearCheckboxes() {
    this.recordManualSelectionChange()
    this.selectedIds = []
    this.setAllCheckboxesChecked(false)
    this.updateSelectedCounter()
    this.updateBulkActionsPanel()
    this.dispatchSelectionChange()
  }

  async selectAll() {
    const selectionVersion = this.selectionVersion

    const fetchedIds = await this.fetchAllIds()

    if (this.isStaleSelection(selectionVersion) || this.isFailedFetch(fetchedIds)) return

    this.applyFetchedIds(fetchedIds)
    this.selectedIds = [ ...this.allIds ]
    this.setAllCheckboxesChecked(true)
  }

  deselectAll() {
    this.selectedIds = []
    this.setAllCheckboxesChecked(false)
  }

  async fetchAllIds() {
    try {
      const url = this.buildFilteredIdsUrl()
      const response = await fetch(url)
      if (!response.ok) throw new Error(`Error loading data: ${response.statusText}`)
      const data = await response.json()

      return data.ids ? Array.from(new Set(data.ids)).map(id => id.toString()) : []
    } catch (error) {
      console.error(`Error loading data: ${error}`)

      return null
    }
  }

  buildFilteredIdsUrl() {
    if (!this.hasFiltersFormTarget)
      return this.idsUrlValue

    const formData = new FormData(this.filtersFormTarget)
    const urlParams = new URLSearchParams()

    for (const [key, value] of formData.entries()) {
      if (value && typeof value === 'string' && value.trim() !== '') {
        urlParams.append(key, value)
      }
    }

    const url = new URL(this.idsUrlValue, window.location.origin)
    url.search = urlParams.toString()

    return url.toString()
  }

  updateSelectedCounter() {
    if (!this.hasCounterTarget) return
    const count = this.selectedIds.length
    this.counterTarget.textContent = count > 0 ? `${count} selected` : ""
  }

  updateBulkActionsPanel() {
    if (!this.hasBulkActionsPanelTarget) return
    this.bulkActionsPanelTarget.classList.toggle("hidden", !this.selectedIds.length)
  }

  updateSelectAllCheckbox() {
    const selectAllCheckbox = this.checkboxTargets[0]
    const total = this.allIds.length
    const selected = this.selectedIds.length

    selectAllCheckbox.checked = this.areAllSelected()
    this.isIndeterminate = selected > 0 && selected !== total
    selectAllCheckbox.indeterminate = this.isIndeterminate
  }

  areAllSelected() {
    if (!this.allIds.length)
      return false

    const allIdsSet = new Set(this.allIds)
    const selectedIdsSet = new Set(this.selectedIds)
    const intersection = allIdsSet.intersection(selectedIdsSet)

    return intersection.size === selectedIdsSet.size && intersection.size === allIdsSet.size
  }

  setAllCheckboxesChecked(checked) {
    this.checkboxTargets.forEach(checkbox => {
      if (!checkbox.disabled) checkbox.checked = checked
    })
  }

  parseCheckboxValue(value) {
    return isNaN(value) ? value : +value
  }

  dispatchSelectionChange() {
    const event = new CustomEvent("tableSelection:change", {
      bubbles: true,
      detail: { selectedIds: this.selectedIds }
    })
    this.element.dispatchEvent(event)
  }
}
